import {
    AutoValue,
    autoValueCreate,
    autoValueIsAutoValue, objectOmit,
    OmitStrict,
    stringTrimPrefix,
    uuidCreate,
    Values
} from '@wix/devzai-utils-common';
import {
    DoppeHideableValue,
    doppeHideableValueCreateHidden,
    doppeHideableValueGetVisibleValue
} from './doppe-hideable-value';
import {DoppeActionGoalType} from '../doppe-action-types/doppe-action-type';
import {WixImageResource} from '@wix/devzai-common-wix';
import {RichTextContent} from '@wix/devzai-utils-react';
import {DoppeActionCtaButtonSettings} from './doppe-action-cta-button-settings';
import {DoppeActionPrice} from './doppe-action-price';
import {
    DoppeCheckoutSettingsWithBindings,
    getDefaultDoppeCheckoutSettingsForAction
} from './doppe-checkout-action-settings';
import {DoppeActionCtaButtonsSettings} from './doppe-action-cta-buttons-settings';

import {DoppeHtmlSitePagesType} from './doppe-html-site';
import {
    BymoPagePropsContentSettingsFilterOptions,
    BymoPagePropsContentSettingsMetadata,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata, bymoPagePropsFilterContentSettings,
    BymoPagePropsSpecialContentSettingMetadata
} from "../bymo-page-props-metadata/bymo-page-props-metadata";

/**
 * The types of CTA buttons that can be used in any action that supports CTA buttons.
 */
export const DoppeActionCommonCTATypes = {
    Link: 'Link',
    Whatsapp: 'Whatsapp',
    Call: 'Call',
    Navigate: 'Navigate',
    Email: 'Email',
    FacebookMessage: 'FacebookMessage',
    InstagramMessage: 'InstagramMessage',
    Share: 'Share',
    Checkout: 'Checkout',
    OpenPage: 'OpenPage',
    IsracardPay: 'IsracardPay',
} as const;

export type DoppeActionCommonCTATypes = Values<typeof DoppeActionCommonCTATypes>;

/**
 * The types of CTA buttons that can be used in specific actions that support CTA buttons.
 */
export const DoppeActionTypeSpecificCTATypes = {
    OpenBlogPost: 'OpenBlogPost',
    OpenProduct: 'OpenProduct',
    OpenService: 'OpenService',
    OpenEvent: 'OpenEvent',
    OpenPricingPlan: 'OpenPricingPlan',
    SaveContact: 'SaveContact',
} as const;

export type DoppeActionTypeSpecificCTATypes = Values<typeof DoppeActionTypeSpecificCTATypes>;

export const DoppeActionCTAType = {
    ...DoppeActionCommonCTATypes,
    ...DoppeActionTypeSpecificCTATypes
} as const;

export type DoppeActionCTAType = Values<typeof DoppeActionCTAType>;

export const DoppeActionCTATypeLinkAutoSource = {
    SameAsSourceURL: 'SameAsSourceURL',
} as const;

export type DoppeActionCTATypeLinkAutoSource = Values<typeof DoppeActionCTATypeLinkAutoSource>;

export const DoppeActionCTATypeLinkOpenType = {
    NewTab: 'NewTab',
    SameTab: 'SameTab',
    InPage: 'InPage',
} as const;

export type DoppeActionCTATypeLinkOpenType = Values<typeof DoppeActionCTATypeLinkOpenType>;

/**
 * Link spec type discriminator - determines what type of link spec object it is
 */
export const DoppeLinkSpecType = {
    /**
     * Internal page (inner page or vertical page)
     */
    Page: 'Page',
    /**
     * Vertical entity item (Product, Blog Post, Event, Service)
     */
    Entity: 'Entity',

    URL: 'Url',
} as const;

export type DoppeLinkSpecType = Values<typeof DoppeLinkSpecType>;

/**
 * Entity types for vertical item links
 */
export const DoppeLinkSpecEntityType = {
    Product: 'Product',
    BlogPost: 'BlogPost',
    Event: 'Event',
    Service: 'Service',
} as const;

export type DoppeLinkSpecEntityType = Values<typeof DoppeLinkSpecEntityType>;

/**
 * Link specification - can be a URL string or an object describing a page/entity
 * 
 * @example
 * // External URL
 * const urlSpec: DoppeLinkSpec = 'https://example.com';
 * 
 * // Page link
 * const pageSpec: DoppeLinkSpec = { type: 'Page', pageId: 'page-123' };
 * 
 * // Entity link
 * const entitySpec: DoppeLinkSpec = { type: 'Entity', entityType: 'Product', entityId: 'prod-456' };
 */
export type DoppeLinkSpec = string | DoppeLinkSpec.Page | DoppeLinkSpec.Entity;

export namespace DoppeLinkSpec {
    /**
     * Page link specification - links to an internal page
     */
    export interface Page {
        type: typeof DoppeLinkSpecType.Page;
        /**
         * The page ID to navigate to (inner page or vertical page ID)
         */
        pageId: string;
    }
    
    /**
     * Entity link specification - links to a vertical entity item
     */
    export interface Entity {
        type: typeof DoppeLinkSpecType.Entity;
        /**
         * The type of entity (Product, BlogPost, Event, Service)
         */
        entityType: DoppeLinkSpecEntityType;
        /**
         * The entity item ID
         */
        entityId: string;
    }
}

/**
 * Type guard to check if a link spec is a string URL
 */
export function doppeLinkSpecIsUrl(linkSpec: DoppeLinkSpec): linkSpec is string {
    return typeof linkSpec === 'string';
}

/**
 * Type guard to check if a link spec is a page link
 */
export function doppeLinkSpecIsPage(linkSpec: DoppeLinkSpec): linkSpec is DoppeLinkSpec.Page {
    return typeof linkSpec === 'object' && linkSpec !== null && linkSpec.type === DoppeLinkSpecType.Page;
}

/**
 * Type guard to check if a link spec is an entity link
 */
export function doppeLinkSpecIsEntity(linkSpec: DoppeLinkSpec): linkSpec is DoppeLinkSpec.Entity {
    return typeof linkSpec === 'object' && linkSpec !== null && linkSpec.type === DoppeLinkSpecType.Entity;
}

/**
 * Gets the link type from a link spec ('Url', 'Page', or 'Entity')
 */
export function doppeLinkSpecGetType(linkSpec: DoppeLinkSpec): DoppeLinkSpecType {
    if (doppeLinkSpecIsUrl(linkSpec)) {
        return DoppeLinkSpecType.URL;
    }
    return linkSpec.type;
}

/**
 * Creates a default empty link spec (empty URL string)
 */
export function doppeLinkSpecCreateDefault(): DoppeLinkSpec {
    return '#';
}

/**
 * Creates a page link spec
 */
export function doppeLinkSpecCreatePage(pageId: string): DoppeLinkSpec.Page {
    return {
        type: DoppeLinkSpecType.Page,
        pageId: pageId
    };
}

/**
 * Creates an entity link spec
 */
export function doppeLinkSpecCreateEntity(entityType: DoppeLinkSpecEntityType, entityId: string): DoppeLinkSpec.Entity {
    return {
        type: DoppeLinkSpecType.Entity,
        entityType: entityType,
        entityId: entityId
    };
}

/**
 * Extracts the destination type from a DoppeLinkSpec for UI purposes
 */
export function doppeLinkSpecGetDestinationType(linkSource: DoppeActionCTATypeLinkSource): DoppeLinkSpecType {
    if (autoValueIsAutoValue(linkSource)) {
        return DoppeLinkSpecType.URL;
    }
    return doppeLinkSpecGetType(linkSource);
}

/**
 * Checks if a link source is a DoppeLinkSpec object (Page or Entity, not a string URL or AutoValue)
 */
export function doppeLinkSpecIsLinkSpecObject(linkSource: DoppeActionCTATypeLinkSource): linkSource is DoppeLinkSpec.Page | DoppeLinkSpec.Entity {
    if (autoValueIsAutoValue(linkSource)) {
        return false;
    }
    return typeof linkSource === 'object' && linkSource !== null && 'type' in linkSource;
}

/**
 * Extracts page ID from a DoppeLinkSpec if it's a page link
 */
export function doppeLinkSpecGetPageId(linkSource: DoppeActionCTATypeLinkSource): string | null {
    if (autoValueIsAutoValue(linkSource)) {
        return null;
    }
    if (doppeLinkSpecIsPage(linkSource)) {
        return linkSource.pageId;
    }
    return null;
}

/**
 * Extracts entity info from a DoppeLinkSpec if it's an entity link
 */
export function doppeLinkSpecGetEntityInfo(linkSource: DoppeActionCTATypeLinkSource): { entityType: DoppeLinkSpecEntityType; entityId: string } | null {
    if (autoValueIsAutoValue(linkSource)) {
        return null;
    }
    if (doppeLinkSpecIsEntity(linkSource)) {
        return {
            entityType: linkSource.entityType,
            entityId: linkSource.entityId
        };
    }
    return null;
}

/**
 * Extracts URL string from a DoppeLinkSpec if it's a URL
 */
export function doppeLinkSpecGetUrlString(linkSource: DoppeActionCTATypeLinkSource): string | null {
    if (autoValueIsAutoValue(linkSource)) {
        return null; // AutoValue means "same as source URL"
    }
    if (doppeLinkSpecIsUrl(linkSource)) {
        return linkSource;
    }
    return null;
}

/**
 * Link source for CTA buttons - can be an AutoValue or a DoppeLinkSpec
 */
export type DoppeActionCTATypeLinkSource = AutoValue<DoppeActionCTATypeLinkAutoSource> | DoppeLinkSpec;


export type DoppeActionCTAButtonSingle = DoppeActionCTAButton.SingleButton;
export type DoppeActionCTAButtonMultiple = DoppeActionCTAButton.MultipleButton;

export type DoppeActionCTAButton = DoppeActionCTAButton.BaseType;

export namespace DoppeActionCTAButton {

    export type BaseType = {
        settings: DoppeActionSettingsCTAButtonSettings;
    }

    export type SingleButton = DoppeActionCTAButton.BaseType & {
        text: DoppeHideableValue<string>;
        showButton: boolean;
    }

    export type MultipleButtonBase = DoppeActionCTAButton.BaseType & {
        isVisible: boolean;
    }

    export type MultipleButton = DoppeActionCTAButton.MultipleButtonBase & {
        text: string;
        isRequired: boolean;
        id: string;
    }

    export type MultipleButtonOverride = DoppeActionCTAButton.MultipleButtonBase & {
        useDefaultContent: boolean;
    }

    export type MultipleButtonWithOverride =
        DoppeActionCTAButton.MultipleButton
        & DoppeActionCTAButton.MultipleButtonOverride
        & {
        isDefaultButton: boolean;
    }
}

export function doppeActionCtaButtonCreateFromListAndOverride(rootId: string, actionCtaButton: DoppeActionCTAButton.MultipleButton, override: DoppeActionCTAButton.MultipleButtonOverride) {
    return {
        ...actionCtaButton,
        ...override,
        isRequired: true,
        id: doppeActionCtaButtonCreateFromListAndOverrideId(rootId, actionCtaButton.id)
    } as DoppeActionCTAButton.MultipleButton;
}

export function doppeActionCtaButtonCreateFromListAndOverrideId(rootId: string, actionCtaButtonId: string) {
    return rootId + actionCtaButtonId;
}

export function doppeActionCtaButtonContactListOverrideId(rootId: string, actionCtaButtonId: string) {
    return stringTrimPrefix(actionCtaButtonId, rootId);
}

export function doppeActionCtaButtonConvertSingleToMultipleButtons(ctaButton: DoppeActionCTAButton.SingleButton) {
    return [{
        text: doppeHideableValueGetVisibleValue(ctaButton.text, ''),
        isVisible: ctaButton.showButton,
        isRequired: true,
        id: uuidCreate(),
        settings: ctaButton.settings
    }] satisfies DoppeActionCTAButton.MultipleButton[];
}

export type DoppeActionSettingsCTAButtonSettings =
    | DoppeActionSettingsCTAButtonSettings.Link
    | DoppeActionSettingsCTAButtonSettings.Whatsapp
    | DoppeActionSettingsCTAButtonSettings.Call
    | DoppeActionSettingsCTAButtonSettings.Navigate
    | DoppeActionSettingsCTAButtonSettings.Email
    | DoppeActionSettingsCTAButtonSettings.InstagramMessage
    | DoppeActionSettingsCTAButtonSettings.FacebookMessage
    | DoppeActionSettingsCTAButtonSettings.SaveContact
    | DoppeActionSettingsCTAButtonSettings.Share
    | DoppeActionSettingsCTAButtonSettings.Checkout
    | DoppeActionSettingsCTAButtonSettings.OpenProduct
    | DoppeActionSettingsCTAButtonSettings.OpenBlogPost
    | DoppeActionSettingsCTAButtonSettings.OpenService
    | DoppeActionSettingsCTAButtonSettings.OpenEvent
    | DoppeActionSettingsCTAButtonSettings.OpenPricingPlan
    | DoppeActionSettingsCTAButtonSettings.OpenPage
    | DoppeActionSettingsCTAButtonSettings.IsracardPay;

export namespace DoppeActionSettingsCTAButtonSettings {

    export type Override = Pick<DoppeActionCTAButtonSingle, 'showButton' | 'settings'>

    type BaseType<T extends DoppeActionCTAType> = {
        type: T;
    }

    export type Link = BaseType<typeof DoppeActionCTAType.Link> & {
        /**
         * Link specification - can be:
         * - AutoValue for "Same as source URL"
         * - A string URL (e.g., 'https://example.com')
         * - A DoppeLinkSpec.Page object (e.g., { type: 'Page', pageId: '...' })
         * - A DoppeLinkSpec.Entity object (e.g., { type: 'Entity', entityType: 'Product', entityId: '...' })
         */
        url: DoppeActionCTATypeLinkSource;
        openType: DoppeActionCTATypeLinkOpenType;
    };

    export type IsracardPay = BaseType<typeof DoppeActionCTAType.IsracardPay> & {
        url: string;
        openType: DoppeActionCTATypeLinkOpenType;
    };

    export type OpenProduct = BaseType<typeof DoppeActionCTAType.OpenProduct> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    export type OpenBlogPost = BaseType<typeof DoppeActionCTAType.OpenBlogPost> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    export type OpenService = BaseType<typeof DoppeActionCTAType.OpenService> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    export type OpenEvent = BaseType<typeof DoppeActionCTAType.OpenEvent> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    export type OpenPricingPlan = BaseType<typeof DoppeActionCTAType.OpenPricingPlan> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    export type OpenPage = BaseType<typeof DoppeActionCTAType.OpenPage> & {
        openType: DoppeActionCTATypeLinkOpenType;
        pageType: DoppeHtmlSitePagesType | null;
    };


    export type Whatsapp = BaseType<typeof DoppeActionCTAType.Whatsapp> & {
        phone: string;
        customMessage: DoppeHideableValue<string>;
    };

    export type Call = BaseType<typeof DoppeActionCTAType.Call> & {
        phone: string;
    };

    export type Email = BaseType<typeof DoppeActionCTAType.Email> & {
        email: string;
        customSubject: DoppeHideableValue<string>;
        customMessage: DoppeHideableValue<string>;
    };

    export type Navigate = BaseType<typeof DoppeActionCTAType.Navigate> & {
        address: string;
    };

    export type FacebookMessage = BaseType<typeof DoppeActionCTAType.FacebookMessage> & {
        userName: string;
        customMessage: DoppeHideableValue<string>
    };

    export type InstagramMessage = BaseType<typeof DoppeActionCTAType.InstagramMessage> & {
        userName: string;
    };

    export type SaveContact = BaseType<typeof DoppeActionCTAType.SaveContact> & {
        notes: string;
        saveContactProfileImage: DoppeHideableValue<WixImageResource | null>
    }

    export type Share = BaseType<typeof DoppeActionCTAType.Share> & {
        url: DoppeActionCTATypeLinkSource;
        title: string;
        description: string;
    }

    export type Checkout = BaseType<typeof DoppeActionCTAType.Checkout> & {
        price: DoppeActionPrice;
        name: DoppeHideableValue<string>;
        description: DoppeHideableValue<RichTextContent>;
        checkoutSettings: DoppeCheckoutSettingsWithBindings;
    }

}

export const DoppeActionCTAButtonsSettingsMetadata = {
    [DoppeActionCTAType.Link]: {
        url: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    },
    [DoppeActionCTAType.Whatsapp]: {
        phone: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Phone, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        customMessage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.Call]: {
        phone: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Phone, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.Navigate]: {
        address: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.Email]: {
        email: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        customSubject: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        customMessage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.InstagramMessage]: {
        userName: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.FacebookMessage]: {
        userName: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        customMessage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.SaveContact]: {
        notes: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        saveContactProfileImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleImage, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.Share]: {
        url: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
        title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.Checkout]: {
        price: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
        name: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        checkoutSettings: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    },
    [DoppeActionCTAType.OpenBlogPost]: {},
    [DoppeActionCTAType.OpenProduct]: {},
    [DoppeActionCTAType.OpenService]: {},
    [DoppeActionCTAType.OpenEvent]: {},
    [DoppeActionCTAType.OpenPricingPlan]: {},
    [DoppeActionCTAType.OpenPage]: {},
    [DoppeActionCTAType.IsracardPay]: {
        url: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
} satisfies {
    [BUTTON in DoppeActionSettingsCTAButtonSettings as BUTTON['type']]: BymoPagePropsContentSettingsMetadata<OmitStrict<BUTTON, 'type'>>;
}

export const DoppeActionCTAButtonGeneralSettingsMetadata = {
    text: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    id: BymoPagePropsSpecialContentSettingMetadata.Ignored,
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionCTAButton.MultipleButton> | BymoPagePropsSpecialContentSettingMetadata;

export function doppeActionCTAButtonFilterContentSettings(
    actionCTAButton: DoppeActionCTAButton.MultipleButton,
    options: BymoPagePropsContentSettingsFilterOptions
) {
    return {
        ...bymoPagePropsFilterContentSettings(objectOmit(actionCTAButton, ['id']), DoppeActionCTAButtonGeneralSettingsMetadata, options),
        settings: bymoPagePropsFilterContentSettings(actionCTAButton.settings, DoppeActionCTAButtonsSettingsMetadata[actionCTAButton.settings.type], options)
    }
}

export function doppeActionMultipleCTAButtonIsVisible(ctaButton: DoppeActionCTAButton.MultipleButton) {
    return ctaButton.isVisible;
}

export function doppeActionCTAButtonIsVisible(ctaButton: DoppeActionCTAButton.SingleButton) {
    return ctaButton.showButton;
}

export function doppeActionCTAButtonCreateLinkSameAsSource(options: {
    text: string,
    hidden?: boolean
}) {

    const {
        text,
        hidden = false
    } = options;

    return {
        settings: {
            type: DoppeActionCTAType.Link,
            url: autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL),
            openType: DoppeActionCTATypeLinkOpenType.SameTab,
        },
        text: text,
        showButton: !hidden
    } satisfies DoppeActionCTAButton.SingleButton;
}

export function doppeActionMultipleCTAButtonCreateLinkSameAsSource(options: {
    text: string,
    hidden?: boolean
    isRequired?: boolean
}) {

    const {
        text,
        hidden = false,
        isRequired = false
    } = options;

    return {
        settings: {
            type: DoppeActionCTAType.Link,
            url: autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL),
            openType: DoppeActionCTATypeLinkOpenType.SameTab,
        },
        text: text,
        isVisible: !hidden,
        isRequired: isRequired,
        id: uuidCreate(),
    } satisfies DoppeActionCTAButton.MultipleButton
}

export function doppeActionMultipleCTAButtonCreate(options: {
    text: string,
    hidden?: boolean
    isRequired?: boolean,
    settings: DoppeActionSettingsCTAButtonSettings;
}) {

    const {
        text,
        hidden = false,
        isRequired = false,
        settings
    } = options;

    return {
        settings: settings,
        text: text,
        isVisible: !hidden,
        isRequired: isRequired,
        id: uuidCreate(),
    } satisfies DoppeActionCTAButton.MultipleButton
}

export function doppeActionCtaButtonTypeGetGoalType(type: DoppeActionCTAType) {

    switch (type) {
        case DoppeActionCTAType.Whatsapp: {
            return DoppeActionGoalType.WhatsappClick;
        }
        case DoppeActionCTAType.Call: {
            return DoppeActionGoalType.CallButtonClick;
        }
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenPage:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPricingPlan:
        case DoppeActionCTAType.Link: {
            return DoppeActionGoalType.Click;
        }
        case DoppeActionCTAType.Email: {
            return DoppeActionGoalType.EmailButtonClick;
        }
        case DoppeActionCTAType.Navigate: {
            return DoppeActionGoalType.NavigateButtonClick;
        }
        case DoppeActionCTAType.FacebookMessage: {
            return DoppeActionGoalType.FacebookMessageButtonClick;
        }
        case DoppeActionCTAType.InstagramMessage: {
            return DoppeActionGoalType.InstagramMessageButtonClick;
        }
        case DoppeActionCTAType.SaveContact: {
            return DoppeActionGoalType.SaveContact;
        }
        case DoppeActionCTAType.Share: {
            return DoppeActionGoalType.Share;
        }
        case DoppeActionCTAType.Checkout: {
            return DoppeActionGoalType.Payment;
        }
        case DoppeActionCTAType.IsracardPay: {
            return DoppeActionGoalType.Payment;
        }
    }
}

export function doppeActionCtaButtonResetSettingsForLockNew<T extends DoppeActionCTAButton.BaseType>(ctaButton: T) {
    const isSameSourceURl = (
            ctaButton.settings.type === DoppeActionCTAType.Link ||
            ctaButton.settings.type === DoppeActionCTAType.Share
        )
        && autoValueIsAutoValue(ctaButton.settings.url);
    ctaButton.settings = doppeActionCtaButtonTypeGetDefaultSettings(ctaButton.settings.type, isSameSourceURl);
}

export function doppeActionCtaButtonResetSettingsForLock(actionSettings: DoppeActionCtaButtonSettings) {
    const isSameSourceURl = (
            actionSettings.ctaButton.settings.type === DoppeActionCTAType.Link ||
            actionSettings.ctaButton.settings.type === DoppeActionCTAType.Share
        )
        && autoValueIsAutoValue(actionSettings.ctaButton.settings.url
        );
    actionSettings.ctaButton.settings = doppeActionCtaButtonTypeGetDefaultSettings(actionSettings.ctaButton.settings.type, isSameSourceURl);
}

export function doppeActionCtaButtonsResetSettingsForLock(actionSettings: DoppeActionCtaButtonsSettings) {
    actionSettings.ctaButtons.forEach(ctaButton => {
        doppeActionCtaButtonResetSettingsForLockNew(ctaButton);
    });
}

export function doppeActionCtaButtonTypeGetDefaultSettings(type: DoppeActionCTAType, hasSourceURL: boolean, hoppPages?:  DoppeHtmlSitePagesType[]) {

    switch (type) {
        case DoppeActionCTAType.Checkout: {
            return {
                type: DoppeActionCTAType.Checkout,
                price: {
                    actualPrice: 20,
                    currencyCode: 'USD'
                },
                name: doppeHideableValueCreateHidden(''),
                description: doppeHideableValueCreateHidden(''),
                checkoutSettings: getDefaultDoppeCheckoutSettingsForAction()
            } satisfies DoppeActionSettingsCTAButtonSettings.Checkout;
        }
        case DoppeActionCTAType.Whatsapp: {
            return {
                type: DoppeActionCTAType.Whatsapp,
                phone: '',
                customMessage: '',
            };
        }
        case DoppeActionCTAType.Call: {
            return {
                type: DoppeActionCTAType.Call,
                phone: '',
            };
        }
        case DoppeActionCTAType.Share:
            return {
                type: DoppeActionCTAType.Share,
                url: hasSourceURL ? autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL) : '',
                title: '',
                description: '',
            };
        case DoppeActionCTAType.Link:
            return {
                type: DoppeActionCTAType.Link,
                url: hasSourceURL ? autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL) : '',
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        case DoppeActionCTAType.IsracardPay:
            return {
                type: DoppeActionCTAType.IsracardPay,
                url: '',
                openType: DoppeActionCTATypeLinkOpenType.InPage,
            };
        case DoppeActionCTAType.Email: {
            return {
                type: DoppeActionCTAType.Email,
                email: '',
                customMessage: '',
                customSubject: ''
            };
        }
        case DoppeActionCTAType.Navigate: {
            return {
                type: DoppeActionCTAType.Navigate,
                address: '',
            };
        }
        case DoppeActionCTAType.FacebookMessage: {
            return {
                type: DoppeActionCTAType.FacebookMessage,
                userName: '',
                customMessage: '',
            };
        }
        case DoppeActionCTAType.InstagramMessage: {
            return {
                type: DoppeActionCTAType.InstagramMessage,
                userName: '',
            };
        }
        case DoppeActionCTAType.SaveContact: {
            return {
                type: DoppeActionCTAType.SaveContact,
                notes: '',
                saveContactProfileImage: doppeHideableValueCreateHidden(null),
            };
        }
        case DoppeActionCTAType.OpenProduct: {
            return {
                type: DoppeActionCTAType.OpenProduct,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.OpenPage: {
            return {
                type: DoppeActionCTAType.OpenPage,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
                pageType: hoppPages?.[0] ?? null,
            };
        }
        case DoppeActionCTAType.OpenBlogPost: {
            return {
                type: DoppeActionCTAType.OpenBlogPost,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.OpenService: {
            return {
                type: DoppeActionCTAType.OpenService,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.OpenEvent: {
            return {
                type: DoppeActionCTAType.OpenEvent,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.OpenPricingPlan: {
            return {
                type: DoppeActionCTAType.OpenPricingPlan,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
    }
}

export function doppeActionCtaButtonTypeOpenUrl(ctaSettings: DoppeActionSettingsCTAButtonSettings) {
    switch (ctaSettings.type) {
        case DoppeActionCTAType.Call:
        case DoppeActionCTAType.Email:
        case DoppeActionCTAType.Whatsapp:
        case DoppeActionCTAType.Navigate:
        case DoppeActionCTAType.FacebookMessage: {
            return true;
        }
        case DoppeActionCTAType.InstagramMessage: {
            return true;
        }
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenPage:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPricingPlan:
        case DoppeActionCTAType.IsracardPay: {
            return ctaSettings.openType !== DoppeActionCTATypeLinkOpenType.InPage;
        }
        case DoppeActionCTAType.Link: {
            // For Link type, check if the url is a LinkSpec object (page/entity)
            const url = ctaSettings.url;
            if (!autoValueIsAutoValue(url) && !doppeLinkSpecIsUrl(url)) {
                // Page and Entity destinations are handled by internal router
                return false;
            }
            return ctaSettings.openType !== DoppeActionCTATypeLinkOpenType.InPage;
        }
        case DoppeActionCTAType.Checkout:
        case DoppeActionCTAType.Share:
        case DoppeActionCTAType.SaveContact: {
            return false;
        }
    }
}

export function doppeActionCtaButtonLinkResolveTarget(ctaSettings: DoppeActionSettingsCTAButtonSettings) {

    switch (ctaSettings.type) {
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenPage:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.Link: {

            if (ctaSettings.openType === DoppeActionCTATypeLinkOpenType.NewTab) {
                return '_blank';
            } else if (ctaSettings.openType === DoppeActionCTATypeLinkOpenType.SameTab) {
                return '_self';
            } else {
                return undefined;
            }
        }
        default: {
            return undefined;
        }
    }
}

